import type { Breadcrumb } from "@sentry/react"

/**
 * Twin of expo/src/common/lib/sentryScrub.ts: keep the two in step.
 *
 * Keeps auth secrets, identity and typed content out of what we send Sentry.
 *
 * A backstop, not the fix: console breadcrumbs and logs are kept because they
 * are the best debugging context an error has, so the log lines themselves are
 * written not to carry PII (see utils/safeError.ts). This catches what slips
 * through in a shape we can recognise: emails, JWTs, bearer and push tokens,
 * long opaque tokens, sensitive query params, credential- and name-shaped
 * keys, and the on-screen text Sentry's tap tracking records.
 *
 * Runs synchronously inside every console call in a release build, so every
 * pattern is bounded and every string is capped before it is scanned. Pure on
 * purpose: `main.tsx` wires it into `beforeBreadcrumb`, and the tests exercise
 * it without Sentry.
 */

export const REDACTED = "[redacted]"

/** Longest string scanned; the rest is cut. Bounds the cost of every regex. */
const MAX_STRING = 2000
/** How deep, and how wide, a logged value is walked. */
const MAX_DEPTH = 4
const MAX_ENTRIES = 50

// Bounded on both sides of the `@` so a long run without one costs O(n), not
// O(n^2). `%40` and `%2540` are the once- and twice-encoded `@`.
const EMAIL_RE =
  /[A-Za-z0-9._%+-]{1,64}(?:@|%40|%2540)[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){0,8}\.([A-Za-z]{2,24})\b/g
// `icon@2x.png` and friends: an asset, not an address.
const FILE_TLDS = new Set([
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
  "svg",
  "json",
  "js",
  "ts",
  "tsx",
  "mp3",
  "mp4",
  "wav",
  "ttf",
  "otf",
])

// A JWT anywhere in free text (access and id tokens are JWTs).
const JWT_RE = /\beyJ[A-Za-z0-9_-]{4,4096}\.[A-Za-z0-9_-]{4,4096}\.[A-Za-z0-9_-]{0,4096}/g

const BEARER_RE = /\b(Bearer)\s+[A-Za-z0-9._~+/=-]{1,4096}/gi

// Expo push tokens, raw or URL-encoded (they sit in a URL path on logout).
const PUSH_TOKEN_RE = /ExponentPushToken(?:\[|%5B)[A-Za-z0-9_-]{1,256}(?:\]|%5D)/gi

// Refresh tokens and magic-link tokens are opaque url-safe strings, not JWTs.
// A run of 40+ url-safe characters with both cases and a digit reads as one;
// hex hashes and UUIDs are single-case, so they survive.
const OPAQUE_RE = /[A-Za-z0-9_~-]{40,4096}/g

// `name=value` pairs inside a query string or fragment. Matched textually so it
// works on any URL shape (relative paths, hash routes) without a URL parser.
const PARAM_RE = /([?&#])([^=&#?\s]{1,128})=([^&#\s]{0,4096})/g

// Query params whose value is a credential or names a person. `search`/`q`
// because the admin panel puts its member search in the URL.
const SENSITIVE_PARAM_RE =
  /token|secret|passw|otp|api[_-]?key|^code$|^state$|^t$|^e_?mail$|^search$|^q$|^query$|^name$/i

// Object keys whose value is a credential or an identity, whatever it looks
// like. A bare `code` or `name` is left alone: as keys they are usually an
// error code and an error or event name.
const SENSITIVE_KEY_RE =
  /token|secret|passw|^pass$|api_?key|authorization|cookie|^otp$|e_?mail|^refresh$|(first|last|full|display|preferred|supporter|user)_?name$/i

function looksLikeOpaqueToken(run: string): boolean {
  return /[a-z]/.test(run) && /[A-Z]/.test(run) && /[0-9]/.test(run)
}

/** The URL with its query string and fragment removed: safe to log. */
export function stripQuery(url: string): string {
  return url.split(/[?#]/)[0]
}

/** Redacts credentials, emails and sensitive params in a string, capped in length. */
export function scrubString(value: string): string {
  const capped = value.length > MAX_STRING ? `${value.slice(0, MAX_STRING)}…[truncated]` : value
  return capped
    .replace(PARAM_RE, (match, sep: string, name: string) =>
      SENSITIVE_PARAM_RE.test(name) ? `${sep}${name}=${REDACTED}` : match,
    )
    .replace(EMAIL_RE, (match, tld: string) => (FILE_TLDS.has(tld.toLowerCase()) ? match : REDACTED))
    .replace(JWT_RE, REDACTED)
    .replace(BEARER_RE, `$1 ${REDACTED}`)
    .replace(PUSH_TOKEN_RE, REDACTED)
    .replace(OPAQUE_RE, (run) => (looksLikeOpaqueToken(run) ? REDACTED : run))
}

function readEntries(value: object): [string, unknown][] {
  const out: [string, unknown][] = []
  for (const key of Object.keys(value).slice(0, MAX_ENTRIES)) {
    try {
      out.push([key, (value as Record<string, unknown>)[key]])
    } catch {
      out.push([key, "[unreadable]"])
    }
  }
  return out
}

/**
 * Deep copy of a logged value with strings scrubbed and credential- or
 * name-shaped keys redacted. Bounded in depth and width, so cycles and huge
 * payloads are safe; binary data is described, not walked.
 */
export function scrubValue(value: unknown, depth = 0): unknown {
  if (typeof value === "string") return scrubString(value)
  if (typeof value === "function" || typeof value === "symbol") return `[${typeof value}]`
  if (value === null || typeof value !== "object") return value
  if (depth >= MAX_DEPTH) return "[truncated]"
  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
    return `[binary ${value.byteLength} bytes]`
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "Invalid Date" : value.toISOString()
  if (value instanceof Error) return scrubString(`${value.name}: ${value.message}`)
  if (value instanceof Map) return `[Map ${value.size}]`
  if (value instanceof Set) return `[Set ${value.size}]`
  if (Array.isArray(value)) {
    const items = value.slice(0, MAX_ENTRIES).map((v) => scrubValue(v, depth + 1))
    if (value.length > MAX_ENTRIES) items.push(`[+${value.length - MAX_ENTRIES} more]`)
    return items
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of readEntries(value)) {
    out[k] = SENSITIVE_KEY_RE.test(k) ? REDACTED : scrubValue(v, depth + 1)
  }
  return out
}

// Sentry's tap tracking (`touch`, and `ui.multiClick` for rage taps) labels the
// tapped element with its accessibility label or on-screen text: an answer, a
// symptom, a supporter's name. Keep which component was tapped, never the label.
const TAP_CATEGORIES = new Set(["touch", "ui.multiClick"])

function scrubTap(breadcrumb: Breadcrumb): Breadcrumb {
  const data: Record<string, unknown> = { ...(breadcrumb.data ?? {}) }
  const path = Array.isArray(data.path)
    ? (data.path as ({ name?: unknown; file?: unknown } | null)[]).map((p) => ({
        name: p?.name,
        file: p?.file,
      }))
    : []
  data.path = path
  delete data.node
  const root = path[0]
  const where = root ? `${String(root.name)}${root.file ? ` (${String(root.file)})` : ""}` : "unknown"
  return {
    ...breadcrumb,
    message: breadcrumb.category === "touch" ? `Touch event within element: ${where}` : where,
    data,
  }
}

/**
 * `beforeBreadcrumb`. Every category keeps its place: tap breadcrumbs lose
 * their labels, and every other breadcrumb has its message scrubbed and its
 * data deep-scrubbed (URLs, logged console arguments, custom `data`).
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.category && TAP_CATEGORIES.has(breadcrumb.category)) return scrubTap(breadcrumb)
  const next: Breadcrumb = { ...breadcrumb }
  if (typeof next.message === "string") next.message = scrubString(next.message)
  if (next.data) next.data = scrubValue(next.data) as Breadcrumb["data"]
  return next
}

/**
 * `beforeSendLog`. Sentry Logs carry every console call (consoleLoggingIntegration)
 * and never pass through `beforeBreadcrumb`, so they get the same scrub.
 */
export function scrubLog<T extends { message?: unknown; attributes?: Record<string, unknown> }>(log: T): T {
  const next = { ...log }
  if (typeof next.message === "string") next.message = scrubString(next.message)
  if (next.attributes) next.attributes = scrubValue(next.attributes) as Record<string, unknown>
  return next
}
